import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  lighten,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import PropTypes from 'prop-types';
import { grey } from '@mui/material/colors';
import PDFViewer from 'components/resume-builder/PDFViewer';
import EnDisclosure from 'assets/documents/english-disclosure.pdf';
import EsDisclosure from 'assets/documents/spanish-disclosure.pdf';
import { FormattedMessage, useIntl } from 'react-intl';
/* Icons */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { submitDebidCard } from 'lib/API/ar-ui';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';

export default function DebitCardCard({ payment_type, setShowEnroll, updatedLesssThan24HoursAgo, setPaymentInfoData }) {
  const intl = useIntl();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contentsAccepted, setContentsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = intl.locale === 'es' ? EsDisclosure : EnDisclosure;
    link.download = `Disclosure-${intl.locale === 'es' ? 'es' : 'en'}.pdf`;
    link.click();
  };
  const queryClient = useQueryClient();

  const headerContent = (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '33px' }}>
      {payment_type !== 'direct_deposit' && <CheckCircleIcon sx={{ color: 'success.main', fontSize: 22 }} />}{' '}
      {payment_type === 'direct_deposit' && <CancelOutlinedIcon sx={{ color: 'error.main', fontSize: 22 }} />}{' '}
      <Typography
        sx={{ color: payment_type !== 'direct_deposit' ? 'success.main' : 'error.main', fontWeight: 700, pl: 0.5 }}
      >
        {intl.formatMessage({
          id:
            payment_type !== 'direct_deposit'
              ? 'fileClaim.bankingModal.currentMethod'
              : 'fileClaim.bankingModal.notInUse',
        })}
      </Typography>
    </Box>
  );

  return (
    <>
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardHeader
          title={headerContent}
          sx={{
            py: 1,
            bgcolor: (theme) =>
              lighten(payment_type !== 'direct_deposit' ? theme.palette.success.main : theme.palette.error.main, 0.95),
          }}
        />
        <CardContent sx={{ borderTopStyle: 'solid', borderTopWidth: 1, borderTopColor: grey['300'] }}>
          <Typography variant="h3" sx={{ textAlign: 'center', pt: 2.5 }}>
            {intl.formatMessage({ id: 'fileClaim.bankingModal.debitCard' })}
          </Typography>

          {payment_type == 'direct_deposit' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: '24px' }}>
              <Button
                variant="contained"
                onClick={() => (updatedLesssThan24HoursAgo ? setShowEnroll(true) : setDeleteDialogOpen(true))}
              >
                {intl.formatMessage({ id: 'fileClaim.bankingForm.switchToDebitCard' })}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
      <Dialog open={deleteDialogOpen} onClose={() => {}} maxWidth="md" fullWidth>
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          {intl.formatMessage({ id: 'fileClaim.bankingForm.switchToDebitCard' })}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              mb: 1,
            }}
          >
            <DialogContentText component="div">
              <FormattedMessage
                id="fileClaim.deleteDialog.text"
                values={{
                  p: (chunks) => <Box>{chunks}</Box>,
                }}
              />
            </DialogContentText>
            <Button onClick={handleDownload} variant="outlined" sx={{ mt: 1, mb: { xs: 1, md: 2 } }}>
              {intl.formatMessage({ id: 'fileClaim.deleteDialog.downloadPdf' })}
            </Button>
          </Box>
          <PDFViewer file={intl.locale === 'es' ? EsDisclosure : EnDisclosure} handleChangePdfUrl={() => {}} />
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: { md: 'space-between' },
            px: 3,
            pb: 1,
          }}
        >
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={contentsAccepted}
                  onChange={(e) => {
                    setContentsAccepted(e.currentTarget.checked);
                  }}
                />
              }
              label={intl.formatMessage({ id: 'uiLaws.confirm' })}
              sx={{ mt: 1 }}
            />
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              mt: { xs: 2, sm: 1, md: 0 },
              mb: { xs: 1, md: 0 },
            }}
          >
            <Button
              variant="outlined"
              autoFocus
              onClick={() => {
                setDeleteDialogOpen(false);
                setContentsAccepted(false);
              }}
              id="delete-go-back-payment-method-button"
            >
              {intl.formatMessage({ id: 'jobSearchActivity.deletePopup.cancel' })}
            </Button>
            <Button
              variant="contained"
              id="delete-yes-experience-button"
              onClick={() => {
                setSubmitting(true);
                submitDebidCard({})
                  .then(() => {
                    setSubmitting(false);
                    setDeleteDialogOpen(false);
                    setPaymentInfoData({ payment_type: 'debit_card' });
                    queryClient.invalidateQueries(['getBankingInfo']);
                  })
                  .catch(() => {
                    setSubmitting(false);
                    enqueueSnackbar(intl.formatMessage({ id: 'app.error' }), { variant: 'error' });
                  });
              }}
              sx={{ ml: 1 }}
              disabled={!contentsAccepted || submitting}
            >
              {intl.formatMessage({ id: 'fileClaim.bankingForm.yesSwitchToDebitCard' })}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}

DebitCardCard.propTypes = {
  payment_type: PropTypes.string,
  setShowEnroll: PropTypes.func,
  setPaymentInfoData: PropTypes.func,
  updatedLesssThan24HoursAgo: PropTypes.bool,
};
